import 'leaflet/dist/images/marker-icon.png';
import 'leaflet/dist/leaflet.css';
import './index.css';

import L, { Layer } from 'leaflet';

import React from 'react';
import { observer } from 'mobx-react';
import { renderToString } from 'react-dom/server';

export const defaultMapCenter = {
    lat: 37.9838,
    lng: 23.7275,
};

export const defaultMapZoom = 10;

export interface MapPoint {
    lat?: number;
    lng?: number;
    id?: number | string;
}

export interface MapComponentProps {
    mapCenter?: MapPoint;
    mapZoom?: number;
    markers?: MapPoint[];
    icon?: React.ReactElement;
    iconClassName?: string;
    maxZoom?: number;
    minZoom?: number;
    showCenter?: boolean;
    disableDragging?: boolean;
    disableZoom?: boolean;
}

const MapComponent: React.FC<MapComponentProps> = observer(
    (props: MapComponentProps) => {
        const _map = React.useRef<L.Map | null>(null);
        const _markers = React.useRef<L.LayerGroup>(new L.LayerGroup());

        const dataAsGeoJSON = React.useCallback(() => {
            const data = props.markers || [];
            const dataGeoJson = data.map((current) => ({
                type: 'Feature' as 'Feature',
                properties: {
                    cluster: false,
                    markerId: current.id || '',
                },
                geometry: {
                    type: 'Point' as 'Point',
                    coordinates: [current.lng, current.lat],
                },
            }));
            if (
                props.showCenter &&
                props.mapCenter?.lat &&
                props.mapCenter?.lng
            ) {
                dataGeoJson.push({
                    type: 'Feature' as 'Feature',
                    properties: {
                        cluster: false,
                        markerId: 'center',
                    },
                    geometry: {
                        type: 'Point' as 'Point',
                        coordinates: [
                            props.mapCenter?.lng || defaultMapCenter.lng,
                            props.mapCenter?.lat || defaultMapCenter.lat,
                        ],
                    },
                });
            }
            return dataGeoJson;
        }, [props.markers, props.mapCenter, props.showCenter]);

        React.useLayoutEffect(() => {
            _markers.current.addLayer(
                L.geoJSON(dataAsGeoJSON() as any, {
                    pointToLayer: props.icon
                        ? createDefaultIcon(
                              renderToString(props.icon),
                              props.iconClassName,
                          )
                        : createDefaultIcon(),
                }),
            );
            if (!_map.current) {
                _map.current = L.map('map-element', {
                    attributionControl: false,
                    zoomControl: !props?.disableZoom,
                    dragging: !props?.disableDragging,
                    layers: [
                        L.tileLayer(
                            'https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&hl=en',
                            {
                                maxZoom: props.maxZoom,
                                minZoom: props.minZoom,
                                subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
                            },
                        ),
                        _markers.current,
                    ],
                });
            }
            _map.current.setView(
                props.mapCenter?.lat && props.mapCenter?.lng
                    ? props.mapCenter
                    : (defaultMapCenter as any),
                props.mapZoom,
            );
        }, [
            dataAsGeoJSON,
            props.disableDragging,
            props.disableZoom,
            props.icon,
            props.iconClassName,
            props.mapCenter,
            props.mapZoom,
            props.maxZoom,
            props.minZoom,
        ]);

        React.useEffect(() => {
            if (_map.current) {
                _markers.current.eachLayer((l: Layer) =>
                    _markers.current.removeLayer(l),
                );
                _markers.current.addLayer(
                    L.geoJSON(dataAsGeoJSON() as any, {
                        pointToLayer: props.icon
                            ? createDefaultIcon(
                                  renderToString(props.icon),
                                  props.iconClassName,
                              )
                            : createDefaultIcon(),
                    }),
                );
            }
        }, [dataAsGeoJSON, props.icon, props.iconClassName]);

        React.useEffect(() => {
            if (
                _map.current &&
                JSON.stringify(props.mapCenter) !==
                    JSON.stringify(defaultMapCenter) &&
                props.mapCenter?.lat &&
                props.mapCenter?.lng
            ) {
                _map.current.flyTo(
                    [props.mapCenter.lat, props.mapCenter.lng],
                    props.mapZoom,
                );
            }
        }, [props.mapCenter, props.mapZoom]);

        return <div className="map-element" id="map-element" />;
    },
);

MapComponent.defaultProps = {
    mapCenter: defaultMapCenter,
    mapZoom: defaultMapZoom,
    maxZoom: 18,
    minZoom: 7,
};

export default MapComponent;
export { MapComponent };

const createDefaultIcon = (v?: string, iconClassName?: string) => {
    return (_feature: any, latlng: any) => {
        const tmp: any = {
            iconUrl: require('leaflet/dist/images/marker-icon.png'),
            iconSize: [iconClassName ? 32 : 24, 32],
            iconAnchor: [9, 24],
            popupAnchor: [0, -6],
            className: iconClassName || '',
        };
        let defaultIcon: any = L.icon(tmp);
        if (v) {
            tmp.html = v;
            tmp.iconUrl = v;
            defaultIcon = L.divIcon(tmp);
        }
        return L.marker({ lat: latlng.lat, lng: latlng.lng } as any, {
            icon: defaultIcon,
        });
    };
};
